import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Slide as SlideAnimation } from 'react-awesome-reveal';
import { HeaderV6 } from '../HeaderV6';
import { Button } from '../Button';
import { TimerV2 } from '../TimerV2';
import { isExpiredPromo } from '../../shared/promo-helpers';
import { QuizProgressBarV4 } from '../QuizProgressBarV4';
import { QuestionAnswerSlideV7 } from '../QuestionAnswerSlideV7';
import { StartSlideV15 } from '../StartSlideV15';
import { PauseSlide } from '../PauseSlide';
import { ProductSlide } from '../ProductSlide';
import { ProfileSlideV5 } from '../ProfileSlideV5';
import { PlanLoaderSlideV9 } from '../PlanLoaderSlideV9';
import { EmailSlideV8 } from '../EmailSlideV8';
import { NameSlide } from '../NameSlide';
import { PreSaleSlide } from '../PreSaleSlide';
import { PlanGraphSlideV9 } from '../PlanGraphSlideV9';
import { SaleV36 } from '../SaleV36';
import { FooterV3 } from '../FooterV3';
import { AgreementTextV5 } from '../AgreementTextV5';
import { useQuiz } from '../../shared/use-quiz';
import { useUserProperties } from '../../shared/use-user-properties';
import { useLegalInfo } from '../../shared/use-legal-info';
import { useSlideEventsV3 } from '../../shared/use-slide-events-v3';
import { getBookArchetype } from '../../shared/book-archetype-helpers';
import { getUserResponse } from '../../shared/answer-helpers';
import {
    EMAIL_SLIDE,
    NAME_SLIDE,
    PRESALE_SLIDE,
    PAUSE_SLIDE,
    PLAN_GRAPH_SLIDE,
    PLAN_LOADER_SLIDE,
    PRODUCT_SLIDE,
    PROFILE_SLIDE,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    START_SLIDE
} from '../../shared/quiz-component-types';
import { preloadImages } from '../../../lib/shared/preload-images';
import emojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v58-2.png';
import tableEmojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v58-20.png';
import footerLogoSrc from '../../assets/images/boundly-logo-pink-light.png';
import styles from './CommonPrelanderV49.css';
import '../../assets/images/emoji-sprites/emoji-sprite-v58-2.css';

const cx = classnames.bind(styles);

const IMAGES = [
    emojiSpriteSrc,
    tableEmojiSpriteSrc
];

const SLIDE_COMPONENTS = {
    [QUESTION_ANSWER_SLIDE_COMPONENT]: QuestionAnswerSlideV7,
    [START_SLIDE]: StartSlideV15,
    [PAUSE_SLIDE]: PauseSlide,
    [PRODUCT_SLIDE]: ProductSlide,
    [PROFILE_SLIDE]: ProfileSlideV5,
    [PLAN_LOADER_SLIDE]: PlanLoaderSlideV9,
    [EMAIL_SLIDE]: EmailSlideV8,
    [NAME_SLIDE]: NameSlide,
    [PRESALE_SLIDE]: PreSaleSlide,
    [PLAN_GRAPH_SLIDE]: PlanGraphSlideV9
};

const DELAY = 350;

function CommonPrelanderV49({
    initialPrelander,
    quizSlides
}) {
    const {
        currentSlideIndex,
        currentSlide,
        results,
        submitAnswer,
        submitTemporaryAnswer,
        temporaryAnswer,
        finished,
        nextSlide,
        backSlide,
        setEmail,
        setExpiryTimestampPromo
    } = useQuiz(quizSlides, initialPrelander);
    const [isExpired, setIsExpired] = useState(isExpiredPromo(results.expiryTimestampPromo));

    const quizSlidesIndexing = quizSlides.filter(slide => !slide.isNotIndexing);
    const isQuestionSlide = currentSlide.component === QUESTION_ANSWER_SLIDE_COMPONENT;
    const isStartSlide = currentSlide.component === START_SLIDE;
    const isEmailSlide = currentSlide.component === EMAIL_SLIDE;
    const indexedSlideNumber = quizSlidesIndexing.indexOf(currentSlide) + 1;
    const { legalLinks, legalEntity } = useLegalInfo();
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});

    useUserProperties(initialPrelander);

    useEffect(() => {
        preloadImages(IMAGES);
    }, []);

    const { logQuizLifecycle } = useSlideEventsV3({ currentSlideIndex: indexedSlideNumber, currentSlide, quizSlides });

    const handleAnswerWithTransition = answer => () => {
        logQuizLifecycle(quizSlidesIndexing.length === indexedSlideNumber, answer);
        submitAnswer(answer);
    };

    const handleContinue = () => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
        logQuizLifecycle(quizSlidesIndexing.length === indexedSlideNumber, temporaryAnswer);

        if (currentSlideIndex === quizSlides.length - 1) {
            setExpiryTimestampPromo();
            setIsExpired(false);
        }

        nextSlide(temporaryAnswer);
    };

    const renderLeftNavigation = () => {
        if (!finished || !results.email || selectedSubscriptionData.plan) {
            return null;
        }

        return (
            <>
                <TimerV2
                    expiryTimestamp={isExpired ? null : results.expiryTimestampPromo}
                    onExpire={() => setIsExpired(true)}
                    className={styles.headerTimer}
                />
                <Button
                    label='Get my plan'
                    type='berry'
                    className={styles.buttonMyPlan}
                    onClick={() => document.getElementById('subscription-start-block')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                />
            </>
        );
    };

    const renderHeader = () => {
        if (merchantData?.merchant) {
            return null;
        }

        const hasBackButton = !finished && Boolean(currentSlideIndex) && !currentSlide?.isNotBackButton;
        const hasPromoHeader = finished && Boolean(results.email);

        return (
            <HeaderV6
                renderLeftNavigation={renderLeftNavigation}
                progressBar={isQuestionSlide && (
                    <QuizProgressBarV4
                        currentSlideIndex={currentSlide.props?.progress?.current ?? indexedSlideNumber}
                        totalSlides={currentSlide.props?.progress?.total ?? quizSlidesIndexing.length}
                    />
                )}
                questionNav={isQuestionSlide && {
                    category: currentSlide.props?.category,
                    hasCounter: !currentSlide.props?.progress,
                    currentSlideIndex: indexedSlideNumber,
                    totalSlides: quizSlidesIndexing.length
                }}
                hasBackButton={hasBackButton}
                onBack={backSlide}
                className={cx('header', {
                    withBorder: finished,
                    withoutPromo: hasPromoHeader && isExpired,
                    withPromo: hasPromoHeader && !isExpired,
                    withPlan: Boolean(selectedSubscriptionData.plan)
                })}
                isFinished={finished && Boolean(results.email) && !selectedSubscriptionData.plan}
                url={window.location.pathname}
            />
        );
    };

    const renderFooter = () => {
        if (finished) {
            if (Boolean(results.email) && !selectedSubscriptionData.plan) {
                return <FooterV3 legalEntity={legalEntity} legalLinks={legalLinks} logoSrc={footerLogoSrc} />;
            }

            return null;
        }

        if (Boolean(currentSlide.continueButton)) {
            return (
                <SlideAnimation
                    delay={500}
                    duration={250}
                    triggerOnce
                    direction='up'
                    className={styles.slideFooter}
                    key={currentSlide.id}
                >
                    <div className={styles.slideFooterWrapper}>
                        <Button
                            type='berry'
                            label={currentSlide.continueButton.label || 'Continue'}
                            onClick={handleContinue}
                            inSlide
                        />
                    </div>
                </SlideAnimation>
            );
        }

        return null;
    };

    const renderContent = () => {
        if (finished) {
            return (
                <SaleV36
                    data={results}
                    expiryTimestamp={results.expiryTimestampPromo}
                    onExpirePromo={() => setIsExpired(true)}
                    isExpired={isExpired}
                    initialPrelander={initialPrelander}
                    selectSubscription={selectSubscription}
                    selectedSubscriptionData={selectedSubscriptionData}
                    merchantData={merchantData}
                    setMerchantData={setMerchantData}
                    legalLinks={legalLinks}
                />
            );
        }

        const Slide = SLIDE_COMPONENTS[currentSlide.component];
        const resultData = (temporaryAnswer?.id || temporaryAnswer?.answerIds)
            ? temporaryAnswer
            : results.userResponses.find(userResponse => userResponse.questionId === currentSlide.id);
        const bookTitle = getUserResponse(results.userResponses, currentSlide.props?.titleSourceId)?.text;
        const archetype = getBookArchetype(results.userResponses);

        return (
            <div
                key={currentSlideIndex}
                className={cx('slide', {
                    withQuestion: isQuestionSlide,
                    withStartSlide: isStartSlide,
                    withEmailSlide: isEmailSlide,
                    withPauseSlide: currentSlide.component === PAUSE_SLIDE,
                    withNameSlide: currentSlide.component === NAME_SLIDE,
                    withPlanLoaderSlide: currentSlide.component === PLAN_LOADER_SLIDE,
                    withProfileSlide: currentSlide.component === PROFILE_SLIDE
                })}
            >
                <Slide
                    {...currentSlide.props}
                    answers={currentSlide.props?.answersByArchetype?.[archetype] || currentSlide.props?.answers}
                    question={currentSlide.props?.question?.replace('“{title}”', bookTitle ? `“${bookTitle}”` : 'your book')}
                    spriteVersion='v58-2'
                    submitAnswer={handleAnswerWithTransition}
                    submitTemporaryAnswer={submitTemporaryAnswer}
                    userResponse={resultData}
                    onContinue={handleContinue}
                    id={currentSlide.id}
                    results={results}
                    delay={DELAY}
                    setEmail={setEmail}
                    legalLinks={legalLinks}
                />
                {isStartSlide && <AgreementTextV5 legalLinks={legalLinks} />}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={cx({ stickyHeader: finished && Boolean(results.email) && !selectedSubscriptionData.plan })}>
                {renderHeader()}
            </div>
            <main
                key={finished}
                className={cx('main', {
                    centered: currentSlide.centered,
                    animated: finished || isEmailSlide
                })}
            >
                {renderContent()}
            </main>
            {renderFooter()}
        </div>
    );
}

export {
    CommonPrelanderV49
};
